import { DomainError } from '../errors.js';
import type { StockStatus } from '../materials/stock.js';

/**
 * Kumanya lotları (ADR 0099 §5–§11): lot bakiyesi defterden türer (`stock_movements.lot_id`
 * toplamı); burada yalnız saf kurallar. Tarihler gemi yerel günüdür (`YYYY-MM-DD`); "bugün" gemi
 * sunucusu saatinden gelir (CLAUDE.md "Zaman").
 */

/** Son kullanma uyarı penceresinin varsayılanı (gün; ADR 0099 §9, soru #1038). */
export const DEFAULT_EXPIRY_WARNING_DAYS = 30;
export const MIN_EXPIRY_WARNING_DAYS = 1;
export const MAX_EXPIRY_WARNING_DAYS = 365;

/** İmha/zayi neden kodları (ADR 0099 §8). */
export const DISPOSAL_REASONS = [
  'expired',
  'damaged',
  'spoiled',
  'temperature_breach',
  'recalled',
  'pest',
  'other',
] as const;
export type DisposalReason = (typeof DISPOSAL_REASONS)[number];

/** Hareketin neden kodları: imha nedenleri ve FEFO sapması. */
export const FEFO_OVERRIDE = 'fefo_override' as const;
export const MOVEMENT_REASON_CODES = [...DISPOSAL_REASONS, FEFO_OVERRIDE] as const;
export type MovementReasonCode = (typeof MOVEMENT_REASON_CODES)[number];

/** Lot kaynakları: mal kabulü satırı, elle giriş, açılış. */
export const LOT_SOURCES = ['goods_receipt_line', 'manual', 'opening'] as const;
export type LotSource = (typeof LOT_SOURCES)[number];

/** Sayımın düzeltme hareketlerinin kaynağı (ADR 0099 §10). */
export const STOCK_COUNT_SOURCE = 'stock_count' as const;

export interface LotBalance {
  id: string;
  batchNo: string | null;
  expiryDate: string | null;
  receivedDate: string;
  createdAt: string;
  balance: number;
}

const PRECISION = 1e6;
const round = (value: number) => Math.round(value * PRECISION) / PRECISION;

function addDays(day: string, days: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}

/** Son kullanma günü kullanılabilir; ertesi gün süresi geçmiştir (soru #1037). */
export function isExpired(expiryDate: string | null, day: string): boolean {
  return expiryDate !== null && expiryDate < day;
}

export const EXPIRY_STATES = ['expired', 'expiring', 'ok', 'none'] as const;
export type ExpiryState = (typeof EXPIRY_STATES)[number];

/** Lotun son kullanma durumu: geçmiş, pencerede (bugün dahil), sorunsuz ya da tarihsiz. */
export function expiryState(
  expiryDate: string | null,
  today: string,
  warningDays: number,
): ExpiryState {
  if (expiryDate === null) return 'none';
  if (expiryDate < today) return 'expired';
  return expiryDate <= addDays(today, warningDays) ? 'expiring' : 'ok';
}

const FAR = '9999-12-31';

/**
 * FEFO sırası (ADR 0099 §7): süresi geçmemiş ve bakiyesi olan lotlar; en erken son kullanma önce,
 * tarihsiz lotlar sonra; eşitlikte önce kabul edilen (FIFO), sonra kayıt anı ve kimlik.
 */
export function fefoOrder(lots: readonly LotBalance[], today: string): LotBalance[] {
  return lots
    .filter((lot) => lot.balance > 0 && !isExpired(lot.expiryDate, today))
    .sort(
      (a, b) =>
        (a.expiryDate ?? FAR).localeCompare(b.expiryDate ?? FAR) ||
        a.receivedDate.localeCompare(b.receivedDate) ||
        a.createdAt.localeCompare(b.createdAt) ||
        a.id.localeCompare(b.id),
    );
}

export interface Allocation {
  /** `null`: lotsuz bakiye (lot öncesi eski hareketler). */
  lotId: string | null;
  quantity: number;
}

/**
 * Çıkışın FEFO dağıtımı: miktar sırayla lotlara bölünür, lotlar yetmezse lotsuz bakiyeden düşer.
 * Yetmezse `NEGATIVE_STOCK` (süresi geçmiş lot sayılmaz: tüketime çıkamaz).
 */
export function allocateFefo(
  lots: readonly LotBalance[],
  quantity: number,
  today: string,
  unassigned = 0,
): Allocation[] {
  let left = round(quantity);
  const result: Allocation[] = [];
  for (const lot of fefoOrder(lots, today)) {
    if (left <= 0) break;
    const take = Math.min(left, lot.balance);
    result.push({ lotId: lot.id, quantity: round(take) });
    left = round(left - take);
  }
  if (left > 0 && unassigned > 0) {
    const take = Math.min(left, unassigned);
    result.push({ lotId: null, quantity: round(take) });
    left = round(left - take);
  }
  if (left > 0) throw new DomainError('NEGATIVE_STOCK', `eksik ${left}`);
  return result;
}

/**
 * Belirli lottan tüketim: lot bilinir, süresi geçmemiş (`LOT_EXPIRED`), bakiyesi yeter
 * (`NEGATIVE_STOCK`). FEFO zorunlu kalemde daha erken son kullanmalı kullanılabilir lot varken
 * gerekçe ister (`FEFO_REASON_REQUIRED`); aynı tarih sapma değildir.
 */
export function checkLotIssue(input: {
  lots: readonly LotBalance[];
  lotId: string;
  quantity: number;
  today: string;
  fefoRequired: boolean;
  reason: string | null;
}): { fefoOverride: boolean } {
  const lot = input.lots.find((l) => l.id === input.lotId);
  if (!lot) throw new DomainError('LOT_UNKNOWN', input.lotId);
  if (isExpired(lot.expiryDate, input.today)) throw new DomainError('LOT_EXPIRED', lot.id);
  if (round(lot.balance - input.quantity) < 0) {
    throw new DomainError('NEGATIVE_STOCK', `lot ${lot.id}: ${lot.balance} < ${input.quantity}`);
  }
  const first = fefoOrder(input.lots, input.today)[0];
  const deviates =
    first !== undefined &&
    (first.expiryDate ?? FAR) < (lot.expiryDate ?? FAR) &&
    first.id !== lot.id;
  if (!deviates || !input.fefoRequired) return { fefoOverride: false };
  if (!input.reason?.trim()) throw new DomainError('FEFO_REASON_REQUIRED', lot.id);
  return { fefoOverride: true };
}

/**
 * Kabulde lot bilgisi (ADR 0099 §6): `expiry_tracking = Yes` son kullanma, `batch_tracking = Yes`
 * batch ister; süresi geçmiş mal kabul edilmez.
 */
export function checkLotReceipt(input: {
  batchTracking: string | null;
  expiryTracking: string | null;
  batchNo: string | null;
  expiryDate: string | null;
  today: string;
}): void {
  if (input.expiryTracking === 'Yes' && !input.expiryDate) {
    throw new DomainError('EXPIRY_REQUIRED');
  }
  if (input.batchTracking === 'Yes' && !input.batchNo?.trim()) {
    throw new DomainError('BATCH_REQUIRED');
  }
  if (isExpired(input.expiryDate, input.today)) {
    throw new DomainError('LOT_EXPIRED', String(input.expiryDate));
  }
}

/**
 * Kumanya stok durumu (ADR 0099 §11; ADR 0030 #54'e yeniden sipariş seviyesi eklenir):
 * `critical` sıfır ya da en azın yarısının altı; `low` en az seviyede ya da yeniden sipariş
 * seviyesinde veya altında; yoksa `normal`.
 */
export function provisionStatus(
  current: number,
  minQty: number,
  reorderLevel: number | null,
): StockStatus {
  if (current <= 0 || current < minQty / 2) return 'critical';
  if ((minQty > 0 && current <= minQty) || (reorderLevel !== null && current <= reorderLevel)) {
    return 'low';
  }
  return 'normal';
}
